import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, ShoppingCart, UserCheck, AlertTriangle, Ban, CreditCard, Scale, Shield } from 'lucide-react';

function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="h-8 w-8 text-green-600" />
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope, sans-serif'}}>
                Obchodné podmienky
              </h1>
            </div>
            
            <p className="text-slate-600 mb-8">
              Posledná aktualizácia: {new Date().toLocaleDateString('sk-SK')}
            </p>

            <div className="space-y-8 text-slate-700">
              
              {/* Úvodné ustanovenia */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">1. Úvodné ustanovenia</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <p>
                    Tieto obchodné podmienky (ďalej len „podmienky") upravujú vzťahy medzi 
                    prevádzkovateľom portálu EVMarket (ďalej len „prevádzkovateľ") a používateľmi 
                    inzertného portálu www.evmarket.sk (ďalej len „portál").
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg my-3">
                    <p><strong>EuroMed Slovensko s.r.o.</strong></p>
                    <p>Svätoplukova 4322/24</p>
                    <p>921 01 Piešťany</p>
                    <p className="mt-2">IČO: 36234478</p>
                    <p>IČ DPH: SK2020170284</p>
                    <p>DIČ: 2020170284</p>
                    <p className="mt-2">Web: www.evmarket.sk</p>
                    <p>E-mail: info@evmarket.sk</p>
                  </div>
                  <p>
                    Portál EVMarket je online platforma určená na inzerciu nových a ojazdených 
                    elektrických vozidiel na Slovensku.
                  </p>
                  <p className="text-sm text-slate-500">
                    Používaním portálu vyjadrujete súhlas s týmito podmienkami.
                  </p>
                </div>
              </section>

              {/* Definície */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">2. Definície pojmov</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Používateľ:</strong> fyzická alebo právnická osoba využívajúca služby portálu</li>
                    <li><strong>Inzerent:</strong> registrovaný používateľ, ktorý zverejňuje inzeráty</li>
                    <li><strong>Inzerát:</strong> ponuka na predaj elektrického vozidla zverejnená na portáli</li>
                    <li><strong>Prémiové služby:</strong> platené reklamné služby (propagácia inzerátov)</li>
                    <li><strong>Ponuka dňa:</strong> exkluzívna prémiová pozícia na hlavnej stránke</li>
                  </ul>
                </div>
              </section>

              {/* Registrácia */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <UserCheck className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">3. Registrácia a užívateľský účet</h2>
                </div>
                <div className="pl-7 space-y-3">
                  <h3 className="font-semibold">3.1 Registrácia</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Registrácia je potrebná pre zverejnenie inzerátov a používanie prémiových služieb</li>
                    <li>Registrácia je možná prostredníctvom e-mailu alebo účtu Google</li>
                    <li>Pri registrácii e-mailom je potrebné overenie e-mailovej adresy</li>
                    <li>Používateľ musí byť starší ako 18 rokov</li>
                  </ul>
                  
                  <h3 className="font-semibold">3.2 Bezpečnosť účtu</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Heslo musí obsahovať minimálne 6 znakov, 1 veľké písmeno, 1 číslo a 1 špeciálny znak</li>
                    <li>Používateľ je zodpovedný za bezpečnosť svojich prihlasovacích údajov</li>
                    <li>V prípade podozrenia na zneužitie účtu kontaktujte prevádzkovateľa</li>
                  </ul>
                  
                  <h3 className="font-semibold">3.3 Zrušenie účtu</h3>
                  <p className="text-sm">
                    Používateľ môže kedykoľvek požiadať o zrušenie účtu e-mailom. 
                    Zrušením účtu sa vymažú všetky aktívne inzeráty.
                  </p>
                </div>
              </section>

              {/* Pravidlá inzercie */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">4. Pravidlá inzercie</h2>
                </div>
                <div className="pl-7 space-y-3">
                  <h3 className="font-semibold">4.1 Základné pravidlá</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Inzercia základných inzerátov je <strong>bezplatná</strong></li>
                    <li>Na portáli je možné inzerovať iba elektrické vozidlá EV</li>
                    <li>Jeden inzerát = jedno vozidlo</li>
                    <li>Inzerát musí obsahovať pravdivé a aktuálne informácie</li>
                    <li>Fotografie musia zobrazovať skutočný stav vozidla</li>
                  </ul>
                  
                  <h3 className="font-semibold">4.2 Obsah inzerátu</h3>
                  <p className="text-sm">Inzerát musí obsahovať minimálne:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Značku a model vozidla</li>
                    <li>Rok výroby</li>
                    <li>Počet najazdených kilometrov</li>
                    <li>Cenu</li>
                    <li>Kontaktné údaje (minimálne e-mail alebo telefón)</li>
                  </ul>
                  
                  <h3 className="font-semibold">4.3 Doba platnosti</h3>
                  <p className="text-sm">
                    Štandardný inzerát je aktívny po dobu 60 dní. 
                    Po uplynutí tejto doby môže byť obnovený.
                  </p>
                </div>
              </section>

              {/* Zakázaný obsah */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Ban className="h-5 w-5 text-red-600" />
                  <h2 className="text-xl font-semibold text-slate-900">5. Zakázaný obsah</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <p>Na portáli je zakázané zverejňovať:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Vozidlá, ktoré nie sú elektrické (benzínové, naftové, CNG)</li>
                    <li>Kradnuté vozidlá alebo vozidlá s nejasným pôvodom</li>
                    <li>Nepravdivé alebo zavádzajúce informácie</li>
                    <li>Duplicitné inzeráty toho istého vozidla</li>
                    <li>Obsah porušujúci autorské práva</li>
                    <li>Nevhodný, urážlivý alebo diskriminačný obsah</li>
                    <li>Reklamu na iné produkty alebo služby</li>
                    <li>Spam alebo automaticky generovaný obsah</li>
                  </ul>
                  <p className="text-sm text-red-600 font-medium mt-2">
                    Porušenie pravidiel môže viesť k odstráneniu inzerátu alebo zrušeniu účtu.
                  </p>
                </div>
              </section>

              {/* Platené služby */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">6. Platené služby</h2>
                </div>
                <div className="pl-7 space-y-3">
                  <h3 className="font-semibold">6.1 Prémiové inzeráty</h3>
                  <p className="text-sm">
                    Prémiové inzeráty sú zobrazované na prednostných pozíciách na hlavnej stránke.
                    Zobrazuje sa maximálne 10 prémiových inzerátov.
                  </p>
                  
                  <h3 className="font-semibold">6.2 Ponuka dňa</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Exkluzívna pozícia na vrchu hlavnej stránky</li>
                    <li>Môže byť aktívna iba jedna „Ponuka dňa" súčasne</li>
                    <li>Maximálna doba rezervácie je 7 dní</li>
                    <li>Ak je pozícia obsadená, zobrazuje sa počet dní do uvoľnenia</li>
                  </ul>
                  
                  <h3 className="font-semibold">6.3 Platobné podmienky</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Platba je realizovaná vopred</li>
                    <li>Akceptujeme platobné karty (Visa, Mastercard)</li>
                    <li>Po úspešnej platbe je služba aktivovaná automaticky</li>
                  </ul>
                  
                  <h3 className="font-semibold">6.4 Reklamácie a vrátenie platby</h3>
                  <p className="text-sm">
                    V prípade technickej chyby na strane prevádzkovateľa máte nárok na vrátenie 
                    alikvotnej časti platby. Reklamáciu podajte e-mailom do 14 dní.
                  </p>
                </div>
              </section>

              {/* Zodpovednosť */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <h2 className="text-xl font-semibold text-slate-900">7. Obmedzenie zodpovednosti</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <p>Prevádzkovateľ:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Nezodpovedá za obsah inzerátov zverejnených používateľmi</li>
                    <li>Nezodpovedá za kvalitu, stav alebo pôvod inzerovaných vozidiel</li>
                    <li>Nezodpovedá za škody vzniknuté z transakcií medzi používateľmi</li>
                    <li>Nezodpovedá za dočasnú nedostupnosť portálu z technických dôvodov</li>
                    <li>Negarantuje úspešný predaj vozidla</li>
                  </ul>
                  <p className="text-sm mt-2">
                    Kupujúci sú povinní si overiť stav vozidla a pravdivosť údajov pred uzavretím transakcie.
                    Odporúčame osobnú prehliadku vozidla a kontrolu dokumentov.
                  </p>
                </div>
              </section>

              {/* Práva prevádzkovateľa */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">8. Práva prevádzkovateľa</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <p>Prevádzkovateľ si vyhradzuje právo:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Odstrániť inzeráty porušujúce tieto podmienky bez predchádzajúceho upozornenia</li>
                    <li>Zablokovať účet používateľa pri opakovanom porušení pravidiel</li>
                    <li>Upraviť alebo odmietnuť nevhodný obsah</li>
                    <li>Zmeniť rozsah alebo ceny platených služieb</li>
                    <li>Prerušiť prevádzku portálu z technických dôvodov</li>
                    <li>Ukončiť prevádzku portálu s 30-dňovým predstihom</li>
                  </ul>
                </div>
              </section>

              {/* Ochrana údajov */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">9. Ochrana osobných údajov</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <p>
                    Spracúvanie osobných údajov sa riadi samostatným dokumentom 
                    <a href="/ochrana-osobnych-udajov" className="text-green-600 hover:underline ml-1">
                      Zásady ochrany osobných údajov
                    </a>.
                  </p>
                </div>
              </section>

              {/* Záverečné ustanovenia */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-5 w-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900">10. Záverečné ustanovenia</h2>
                </div>
                <div className="pl-7 space-y-2">
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Tieto podmienky sa riadia právnym poriadkom Slovenskej republiky</li>
                    <li>Spory budú riešené príslušnými súdmi SR</li>
                    <li>Prevádzkovateľ si vyhradzuje právo tieto podmienky kedykoľvek zmeniť</li>
                    <li>O zmenách budú používatelia informovaní e-mailom alebo oznámením na webe</li>
                    <li>Pokračovaním v používaní portálu po zmene podmienok vyjadrujete súhlas s novými podmienkami</li>
                  </ul>
                  <p className="text-sm text-slate-500 mt-4">
                    Tieto obchodné podmienky nadobúdajú účinnosť dňom ich zverejnenia na portáli.
                  </p>
                </div>
              </section>

              {/* Kontakt */}
              <section className="bg-slate-50 p-4 rounded-lg">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Kontakt</h2>
                <p className="text-sm">
                  V prípade otázok k týmto podmienkam nás kontaktujte na: 
                  <strong className="ml-1">info@evmarket.sk</strong>
                </p>
              </section>

            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default TermsPage;
